import React, { Component } from 'react';
import ReactTable from 'react-table';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import i18n from '../../../../Utils/i18n';
import { getRecordingDetailsTableColumnOptions } from '../../../../Components/CheckedMenu/CheckedMenuItemsHelper';
import SimpleSearchBox from '../../../../Components/SearchBox/SimpleSearchBox';
import CheckedMenu from '../../../../Components/CheckedMenu/CheckedMenu';
import Menu from '../../../../Components/Menu/Menu';
import { HeaderSortComponent } from '../../../../Scss/Custom/ReactTable/HeaderSortComponent';
import './RecordingViewsTable.css';
import { RECORDING_DETAILS, RECORDING_VIEW_SEARCH } from '../../../../Telemetry/Mixpanel/Constants/mixpanelConstants';
import DownloadIcon from '../../../../Components/Buttons/DownloadIcon';
import {
  RecordingSource,
  RecordingViewsVisibleColumns,
  ReportFormat,
  ReportType,
  XLS
} from '../../../../Utils/Constants';
import { toggleElement } from '../../../../Utils/ArrayUtils';
import XLSX from 'xlsx';
import { getUnion } from '../../../../Utils/ArrayUtils';
import { getReportTypeMenuItems } from '../../../../Components/Menu/MenuItemsHelper';
import { getTimeInHrMinAndSecFromMillis } from '../../../../Utils/DateFormatUtils';
import { dateSorter } from '../../../../Utils/MomentUtils';
import { trackReportGeneration } from '../../../../Telemetry/Mixpanel/Features/analyticsTracker';

class RecordingViewsTable extends Component {
  static contextTypes = {
    t: PropTypes.func.isRequired
  };

  static propTypes = {
    isBig: PropTypes.bool,
    recordingSource: PropTypes.string,
    views: PropTypes.array,
    searchByName: PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      visibleColumns: [
        RecordingViewsVisibleColumns.FIRST_NAME,
        RecordingViewsVisibleColumns.LAST_NAME,
        RecordingViewsVisibleColumns.EMAIL,
        RecordingViewsVisibleColumns.LAST_WATCH_DATE,
        RecordingViewsVisibleColumns.AVG_PERC_VIEWED,
        RecordingViewsVisibleColumns.VIEW_COUNT,
        RecordingViewsVisibleColumns.VIEW_DURATION,
        RecordingViewsVisibleColumns.SOURCE_EMBEDDED_LINK
      ],
      textString: ''
    };
  }

  getHeaders = recordingSource => {
    const { t } = this.context;
    const columns = this.getColumns(recordingSource);
    const header = [];
    columns.forEach(({ show, accessor }) => {
      if (show) {
        switch (accessor) {
          case ViewsTableAccessor.GIVEN_NAME:
            header.push([t('recordingDetails.firstName'), accessor]);
            break;
          case ViewsTableAccessor.SUR_NAME:
            header.push([t('recordingDetails.lastName'), accessor]);
            break;
          case ViewsTableAccessor.EMAIL:
            header.push([t('recordingDetails.email'), accessor]);
            break;
          case ViewsTableAccessor.VIEW_DATE:
            header.push([t('recordingDetails.viewDate'), accessor]);
            break;
          case ViewsTableAccessor.VIEW_COUNT:
            header.push([t('recordingDetails.viewCount'), accessor]);
            break;
          case ViewsTableAccessor.WATCH_TIME_IN_PERCENT:
            header.push([t('recordingDetails.avgPercentageViewed'), accessor]);
            break;
          case ViewsTableAccessor.VIEW_DURATION_IN_MILLIS:
            header.push([t('recordingDetails.viewDuration'), accessor]);
            break;
          case ViewsTableAccessor.SOURCE_EMBEDDED_LINK:
            header.push([t('recordingDetails.parentUrl'), accessor]);
            break;
          default:
            console.error('unexpected case');
            break;
        }
      }
    });
    return header;
  };

  getRecords = (views, headers) => {
    const data = [];

    const reducedViews = views.reduce((acc, curr) => {
      let existingView = acc[curr.email];
      if (existingView) {
        existingView.viewDate = [curr.viewDate, existingView.viewDate].sort(dateSorter).reverse()[0];
        existingView.viewCount += 1;
        acc[curr.email] = existingView;
      } else {
        acc[curr.email] = Object.assign(Object.create(Object.getPrototypeOf(curr)), curr);
      }
      return acc;
    }, {});

    views.forEach(view => {
      if (!reducedViews[view.email]) {
        return true;
      }
      const aggregatedView = reducedViews[view.email];
      let row = [];
      headers.forEach(element => {
        switch (element) {
          case ViewsTableAccessor.VIEW_DATE:
            row.push(moment(aggregatedView[element]).format(i18n.t('formatting.date')));
            break;
          case ViewsTableAccessor.WATCH_TIME_IN_PERCENT:
            row.push(`${aggregatedView[element]} %`);
            break;
          case ViewsTableAccessor.VIEW_DURATION_IN_MILLIS:
            row.push(getTimeInHrMinAndSecFromMillis(aggregatedView[element]));
            break;
          default:
            row.push(aggregatedView[element]);
        }
      });
      data.push(row);
      delete reducedViews[view.email];
    });
    return data;
  };

  exportFile = (views, recordingSource, id) => {
    const { t } = this.context;
    const headers = this.getHeaders(recordingSource);
    const records = this.getRecords(views, headers.map(item => item[1]));
    const data = getUnion([headers.map(item => item[0])], records);
    const workBook = XLSX.utils.book_new();
    const dataSheet = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(workBook, dataSheet);
    trackReportGeneration(ReportType.RECORDING_VIEWS_REPORT, id === XLS ? ReportFormat.XLSX : ReportFormat.CSV);
    XLSX.writeFile(workBook, `${t('recordingDetails.viewsReport')}.${id === XLS ? 'xlsx' : 'csv'}`);
  };

  render() {
    const { recordingSource, views, searchByName, isBig } = this.props;
    const detailsMenu = getRecordingDetailsTableColumnOptions(recordingSource).map(item => {
      return {
        onClick: () => this.onClickHandler(item.id),
        text: item.title,
        id: item.id,
        checked: this.state.visibleColumns.includes(item.id)
      };
    });

    const detailsDownloadMenu = [];
    const menuDownloadItems = getReportTypeMenuItems();
    menuDownloadItems.map(item =>
      detailsDownloadMenu.push({
        onClick: () => this.exportFile(views, recordingSource, item.id),
        text: item.title,
        iconClassName: item.iconClass,
        id: item.id
      })
    );

    const { t } = this.context;
    const mixpanelData = {
      action: RECORDING_DETAILS,
      searchType: RECORDING_VIEW_SEARCH
    };
    return (
      <div className="recordingViewsTable">
        <div className="tableSearchRow d-flex justify-content-between align-items-center">
          <SimpleSearchBox
            searchString={this.state.textString}
            placeHolder={t('recordingDetails.searchViewers')}
            onSearchInputChange={searchByName}
            mixpanelData={mixpanelData}
          />
          <div className="d-flex justify-content-between align-items-center">
            <CheckedMenu
              isBig={isBig}
              menuKey="manageColumns"
              buttonName={t('sessionDetails.manageColumns')}
              detailsMenu={detailsMenu}
              containerClassName="recordingDetailsManageColumn"
            />
            <Menu
              isBig={isBig}
              menuKey="downloadRecordingViews"
              buttonName={t('buttons.downloadCamelCase')}
              detailsMenu={detailsDownloadMenu}
              buttonComponent={<DownloadIcon isBig={isBig} />}
            />
          </div>
        </div>
        <div className="recordingsTableSection">{this.getRecordingDetailsTable(views, recordingSource)}</div>
      </div>
    );
  }

  getRecordingDetailsTable = (views, recordingSource) => {
    return (
      <ReactTable
        data={views}
        pivotBy={['email']}
        columns={this.getColumns(recordingSource)}
        defaultPageSize={views ? views.length : 0}
        minRows={10}
        minColumns={5}
        style={{ height: '540px' }}
        showPagination={false}
        className="-highlight"
        defaultSorted={[{ id: 'viewDate', desc: true }]}
      />
    );
  };

  getColumns = recordingSource => {
    const { t } = this.context;
    const defaultColumns = [
      {
        accessor: ViewsTableAccessor.GIVEN_NAME,
        Header: <HeaderSortComponent headerTitle={t('recordingDetails.firstName')} />,
        show: this.state.visibleColumns.includes(RecordingViewsVisibleColumns.FIRST_NAME),
        aggregate: vals => vals[0]
      },
      {
        accessor: ViewsTableAccessor.SUR_NAME,
        Header: <HeaderSortComponent headerTitle={t('recordingDetails.lastName')} />,
        show: this.state.visibleColumns.includes(RecordingViewsVisibleColumns.LAST_NAME),
        aggregate: vals => vals[0]
      },
      {
        accessor: ViewsTableAccessor.EMAIL,
        Header: <HeaderSortComponent headerTitle={t('recordingDetails.email')} />,
        show: this.state.visibleColumns.includes(RecordingViewsVisibleColumns.EMAIL),
        width: 220,
        Expander: ({ isExpanded }) => (
          <span>
            <i className={`togo-icon togo-icon-chevron ${isExpanded ? '' : 'togo-icon-rotate-270'}`} />
            &nbsp;
          </span>
        ),
        PivotValue: cell => {
          return <span className="fakeLink">{cell.value}</span>;
        }
      },
      {
        accessor: ViewsTableAccessor.VIEW_DATE,
        Header: <HeaderSortComponent headerTitle={t('recordingDetails.viewDate')} />,
        show: this.state.visibleColumns.includes(RecordingViewsVisibleColumns.LAST_WATCH_DATE),
        aggregate: vals => vals.sort(dateSorter).reverse()[0],
        Aggregated: row => `${this.getFormattedViewDate(row)}`,
        Cell: this.getFormattedViewDate
      },
      {
        accessor: ViewsTableAccessor.VIEW_COUNT,
        Header: <HeaderSortComponent headerTitle={t('recordingDetails.viewCount')} />,
        show: this.state.visibleColumns.includes(RecordingViewsVisibleColumns.VIEW_COUNT),
        aggregate: vals => vals.reduce((acc, curr) => acc + curr)
      }
    ];
    const g2sColumns = [
      {
        accessor: ViewsTableAccessor.WATCH_TIME_IN_PERCENT,
        Header: <HeaderSortComponent headerTitle={t('recordingDetails.avgPercentageViewed')} />,
        show: this.state.visibleColumns.includes(RecordingViewsVisibleColumns.AVG_PERC_VIEWED),
        Cell: ({ groupedByPivot, value }) => `${groupedByPivot ? `${value} %` : ''}`,
        aggregate: vals => vals[0]
      },
      {
        accessor: ViewsTableAccessor.VIEW_DURATION_IN_MILLIS,
        Header: <HeaderSortComponent headerTitle={t('recordingDetails.viewDuration')} />,
        show: this.state.visibleColumns.includes(RecordingViewsVisibleColumns.VIEW_DURATION),
        Cell: ({ groupedByPivot, value }) => `${groupedByPivot ? `${getTimeInHrMinAndSecFromMillis(value)}` : ''}`,
        aggregate: vals => vals[0]
      }
    ];

    const g2sEmbeddedColumns = [
      {
        accessor: ViewsTableAccessor.SOURCE_EMBEDDED_LINK,
        Header: <HeaderSortComponent headerTitle={t('recordingDetails.parentUrl')} />,
        show: this.state.visibleColumns.includes(RecordingViewsVisibleColumns.SOURCE_EMBEDDED_LINK),
        aggregate: vals => vals[0],
        className: 'wordBreakColumn'
      }
    ];

    const extraColums =
      RecordingSource.G2S === recordingSource
        ? g2sColumns
        : RecordingSource.G2S_EMBEDDED === recordingSource
        ? g2sEmbeddedColumns
        : [];
    return [...defaultColumns, ...extraColums];
  };

  getFormattedViewDate = ({ value }) => moment(value).format(i18n.t('formatting.date'));

  onClickHandler = updateId =>
    this.setState(state => ({ visibleColumns: toggleElement(updateId, state.visibleColumns) }));
}

export default RecordingViewsTable;

class ViewsTableAccessor {
  static get GIVEN_NAME() {
    return 'givenName';
  }
  static get SUR_NAME() {
    return 'surName';
  }
  static get EMAIL() {
    return 'email';
  }
  static get VIEW_DATE() {
    return 'viewDate';
  }
  static get VIEW_COUNT() {
    return 'viewCount';
  }
  static get WATCH_TIME_IN_PERCENT() {
    return 'watchTimeInPercent';
  }
  static get VIEW_DURATION_IN_MILLIS() {
    return 'viewDurationInMillis';
  }
  static get SOURCE_EMBEDDED_LINK() {
    return 'sourceEmbeddedLink';
  }
}



// WEBPACK FOOTER //
// ./src/GoToWebinar/Recordings/RecordingDetails/Table/RecordingViewsTable.js